import { useCallback, useEffect, useRef, useState } from "react";
import { useVoiceVisualizer } from "react-voice-visualizer";
import { useTranscribeAudio } from "@queries/chat/useTranscribeAudio";
import {
  getRecordingFileExtension,
  pickRecordingMimeType,
} from "@shared/utils/audio-recording";

// Chat voice messages are short, and a shorter clip also keeps the upload small.
const MAX_RECORDING_SECONDS = 180;

// Asks the encoder for a modest bitrate so a full-length clip lands around 1.4MB. Browsers
// treat this as a target rather than a limit, so it shrinks typical uploads but guarantees
// nothing about the result's size.
const AUDIO_BITS_PER_SECOND = 64000;

// Mirrors the limit the backend enforces on /audio/transcriptions. Checked here only so an
// oversized clip fails immediately with a useful message instead of after a doomed upload.
const MAX_CLIP_BYTES = 5 * 1024 * 1024;


// A stopped take isn't finished: its clip is delivered later, and the recorder keeps a
// single shared reference to the in-flight recording, so starting the next take before the
// previous clip lands makes the late clip tear down the new recorder. A take stays pending
// from the moment it's stopped until its clip arrives, and no new take may begin until then.
type PendingTake = "none" | "keep" | "drop";

type Params = {
  onPermissionError: () => void;
  onUnsupported: () => void;
  onTranscriptReady: (transcript: string) => void;
  onEmptyResult?: () => void;
  onTranscriptionError?: () => void;
  onClipTooLarge?: () => void;
};

export const useVoiceTranscription = ({
  onPermissionError,
  onUnsupported,
  onTranscriptReady,
  onEmptyResult,
  onTranscriptionError,
  onClipTooLarge,
}: Params) => {
  const [mimeType] = useState(pickRecordingMimeType);
  const [pendingTake, setPendingTakeState] = useState<PendingTake>("none");
  const pendingTakeRef = useRef<PendingTake>("none");
  const setPendingTake = useCallback((next: PendingTake) => {
    pendingTakeRef.current = next;
    setPendingTakeState(next);
  }, []);
  const { mutate: transcribe, isPending: isTranscribing } =
    useTranscribeAudio();

  const onPermissionErrorRef = useRef(onPermissionError);
  onPermissionErrorRef.current = onPermissionError;
  const onTranscriptReadyRef = useRef(onTranscriptReady);
  onTranscriptReadyRef.current = onTranscriptReady;
  const onEmptyResultRef = useRef(onEmptyResult);
  onEmptyResultRef.current = onEmptyResult;
  const onTranscriptionErrorRef = useRef(onTranscriptionError);
  onTranscriptionErrorRef.current = onTranscriptionError;
  const onClipTooLargeRef = useRef(onClipTooLarge);
  onClipTooLargeRef.current = onClipTooLarge;

  const {
    error: recorderError,
    isRecordingInProgress,
    isProcessingStartRecording,
    recordedBlob,
    recordingTime,
    startRecording,
    stopRecording,
  } = useVoiceVisualizer({
    mediaRecorderOptions: mimeType
      ? { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
      : undefined,
    shouldHandleBeforeUnload: false,
  });

  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;

  useEffect(() => {
    if (!isRecordingInProgress) return;
    if (recordingTime < MAX_RECORDING_SECONDS * 1000) return;
    setPendingTake("keep");
    stopRecording();
  }, [isRecordingInProgress, recordingTime, stopRecording, setPendingTake]);

  useEffect(() => {
    if (!recorderError) return;
    setPendingTake("none");
    onPermissionErrorRef.current();
  }, [recorderError, setPendingTake]);

  useEffect(() => {
    const take = pendingTakeRef.current;
    if (take === "none" || !recordedBlob) return;
    // Released even for a clip we won't use, so an empty or dropped take can't wedge the mic.
    setPendingTake("none");
    if (take === "drop" || !mimeType || recordedBlob.size === 0) return;
    if (recordedBlob.size > MAX_CLIP_BYTES) {
      onClipTooLargeRef.current?.();
      return;
    }

    const file = new File(
      [recordedBlob],
      `voice-message-${Date.now()}.${getRecordingFileExtension(mimeType)}`,
      { type: mimeType },
    );
    transcribe(
      { audio: file },
      {
        onSuccess: (text) => {
          const transcript = text.trim();
          if (transcript) {
            onTranscriptReadyRef.current(transcript);
          } else {
            onEmptyResultRef.current?.();
          }
        },
        onError: () => onTranscriptionErrorRef.current?.(),
      },
    );
  }, [recordedBlob, mimeType, transcribe, setPendingTake]);

  useEffect(() => () => stopRecordingRef.current(), []);

  const start = () => {
    if (!mimeType) {
      onUnsupported();
      return;
    }
    if (pendingTakeRef.current !== "none") return;
    startRecording();
  };

  const stop = () => {
    setPendingTake("keep");
    stopRecording();
  };

  const discard = () => {
    setPendingTake("drop");
    stopRecording();
  };

  return {
    isSupported: mimeType !== undefined,
    isListening: isRecordingInProgress,
    // True only while the browser is opening the mic, which is the window where speech
    // genuinely isn't captured yet. Recording is already live once this clears.
    isStarting: isProcessingStartRecording,
    // A kept clip is on its way to becoming a message, so the composer shouldn't be edited
    // or submitted into it. A discarded one is going nowhere and holds nothing up.
    isProcessing: pendingTake === "keep" || isTranscribing,
    // The recorder itself can't take a new clip until the previous one has been delivered,
    // whichever way that take ended.
    canRecord: pendingTake === "none" && !isTranscribing,
    start,
    stop,
    discard,
  };
};
